import { collection, doc, writeBatch, where, query, getDocs, arrayRemove, arrayUnion, increment } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from '../utils/objectUtils';
import { v4 as uuid } from 'uuid';
import moment from 'moment-timezone';
import { createWithId, getAll, getById, getByQuery, getBySingleQuery, getReference, remove, update, updateField, updateFields } from '../utils/dbUtils';
import Task from './Task';

class TaskReminder {
    static collectionName = 'taskReminders';

    id = uuid();
    taskId = null;
    userId = null;
    type = 'DEADLINE_WARNING'; // DEADLINE_WARNING, FOLLOW_UP, OTHER
    message = null;
    reminderAt = null;
    isSent = false;
    sentAt = null;
    methods = ['PUSH']; // PUSH, EMAIL, SMS
    metadata = {};

    constructor(reminder) {
        if (isObjectNotEmpty(reminder)) {
            if (isStringNotEmpty(reminder.id)) { this.id = reminder.id; }
            if (isStringNotEmpty(reminder.taskId)) { this.taskId = reminder.taskId; }
            if (isStringNotEmpty(reminder.userId)) { this.userId = reminder.userId; }
            if (isStringNotEmpty(reminder.type)) { this.type = reminder.type; }
            if (isStringNotEmpty(reminder.message)) { this.message = reminder.message; }
            if (isNotNullOrUndefined(reminder.reminderAt)) { this.reminderAt = reminder.reminderAt; }
            if (isBooleanTrue(reminder.isSent)) { this.isSent = true; }
            if (isNotNullOrUndefined(reminder.sentAt)) { this.sentAt = reminder.sentAt; }
            if (isListNotEmpty(reminder.methods)) { this.methods = reminder.methods; }
            if (isObjectNotEmpty(reminder.metadata)) { this.metadata = reminder.metadata; }
        }
    }

    toDto = () => {
        return {
            taskId: this.taskId,
            userId: this.userId,
            type: this.type,
            message: this.message,
            reminderAt: this.reminderAt,
            isSent: this.isSent,
            sentAt: this.sentAt,
            methods: this.methods,
            metadata: this.metadata
        };
    }

    isValid = () => {
        if (isStringEmpty(this.taskId)) throw new Error('TaskId is required');
        if (isStringEmpty(this.userId)) throw new Error('UserId is required');
        if (isStringEmpty(this.message)) throw new Error('Message is required');
        if (!this.reminderAt) throw new Error('Reminder time is required');
        return true;
    }

    create = async () => {
        if (this.isValid()) {
            const batch = writeBatch(db);

            // Create reminder
            const reminderRef = doc(collection(db, TaskReminder.collectionName), this.id);
            const reminderDto = {
                ...this.toDto(),
                createdAt: moment().utc().toDate(),
                createdBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM',
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM',
                isDeleted: false
            };
            batch.set(reminderRef, reminderDto);

            // Update task with reminder reference
            const taskRef = doc(collection(db, Task.collectionName), this.taskId);
            batch.update(taskRef, {
                reminderIds: arrayUnion(this.id),
                remindersCount: increment(1),
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            await batch.commit();

            return { id: this.id, ...reminderDto };
        }
    }

    static markAsSent = async (id) => {
        if (isStringNotEmpty(id)) {
            return updateFields(TaskReminder.collectionName, id, {
                isSent: true,
                sentAt: moment().utc().toDate()
            });
        }
        return false;
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(TaskReminder.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id) && isObjectNotEmpty(updatedFields)) {
            return await updateFields(TaskReminder.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedEntity, oldObj) => {
        const updatedObj = new TaskReminder(updatedEntity);
        if (updatedObj.isValid() && id === updatedEntity.id) {
            const success = await update(TaskReminder.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() };
            }

            return null;
        } else {
            throw new Error('id and id field of the updated reminder must match.')
        }
    }

    static delete = async (id, taskId) => {
        if (isStringNotEmpty(id) && isStringNotEmpty(taskId)) {
            const batch = writeBatch(db);

            // Delete reminder
            const reminderRef = doc(collection(db, TaskReminder.collectionName), id);
            batch.delete(reminderRef);

            // Update task
            const taskRef = doc(collection(db, 'tasks'), taskId);
            batch.update(taskRef, {
                reminderIds: arrayRemove(id),
                remindersCount: increment(-1),
                modifiedAt: moment().utc().toDate(),
                modifiedBy: isObjectNotEmpty(auth.currentUser) ? auth.currentUser.uid : 'SYSTEM'
            });

            await batch.commit();

            return true;
        } else {
            throw new Error("id and taskId are required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(TaskReminder.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(TaskReminder.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(TaskReminder.collectionName, fieldName, fieldValue, comparer);
    }

    static getPendingReminders = async () => {
        const now = moment().utc().toDate();

        // Get reminders that:
        // 1. Haven't been sent
        // 2. Reminder time is now or in the past
        // 3. Task is not cancelled or completed
        const q = query(
            collection(db, TaskReminder.collectionName),
            where('isSent', '==', false),
            where('reminderAt', '<=', now)
        );

        let reminders = [];
        const remindersList = await getByQuery(q, TaskReminder.collectionName);
        for (const reminder of remindersList) {
            // Check task status
            if (isStringNotEmpty(reminder.taskId)) {
                const taskRef = doc(collection(db, Task.collectionName), reminder.taskId);
                const task = await getReference(taskRef);

                if (isObjectNotEmpty(task)) {
                    // Only include reminder if task is still active
                    if (task.status !== 'COMPLETED' && task.status !== 'CANCELLED') {
                        reminders.push(reminder);
                    } else {
                        // If task is completed/cancelled, mark reminder as sent
                        await TaskReminder.markAsSent(reminder.id);
                    }
                }
            }
        }

        return reminders;
    }

    static getByTaskId = async (taskId) => {
        return await getBySingleQuery(TaskReminder.collectionName, 'taskId', taskId, '==');
    }
}

export default TaskReminder;